import type { OpaqueCursor, Paginated, PaginationQuery } from '@luacentrix/contracts';

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGES_PER_ITERATION = 200;

export type PageFetcher<T> = (query: PaginationQuery) => Promise<Paginated<T>>;

export interface IterationOptions<T> {
  limit?: number | undefined;
  cursor?: OpaqueCursor | undefined;
  maxPages?: number | undefined;
  onProgress?: ((page: Paginated<T>, pageNumber: number) => void) | undefined;
}

export function mergeQuery(
  base: Record<string, string | undefined>,
  options: IterationOptions<unknown>,
): Record<string, string> {
  const query: Record<string, string> = {};
  for (const [key, value] of Object.entries(base)) {
    if (value !== undefined) query[key] = value;
  }
  query.limit = String(options.limit ?? DEFAULT_PAGE_SIZE);
  if (options.cursor !== undefined && options.cursor !== '') {
    query.cursor = options.cursor;
  }
  return query;
}

export async function* iteratePages<T>(
  fetchPage: PageFetcher<T>,
  options: IterationOptions<T> = {},
): AsyncGenerator<T, void, undefined> {
  const maxPages = options.maxPages ?? MAX_PAGES_PER_ITERATION;
  let cursor = options.cursor;
  let pageNumber = 0;

  while (pageNumber < maxPages) {
    const page = await fetchPage({
      limit: options.limit ?? DEFAULT_PAGE_SIZE,
      ...(cursor === undefined ? {} : { cursor }),
    });
    pageNumber += 1;
    options.onProgress?.(page, pageNumber);
    for (const item of page.items) {
      yield item;
    }
    if (!page.has_more || page.next_cursor === null) {
      return;
    }
    cursor = page.next_cursor;
  }
}

export async function collectPages<T>(
  fetchPage: PageFetcher<T>,
  options: IterationOptions<T> = {},
): Promise<{ items: T[]; pages: number; stopped_reason: 'exhausted' | 'max_pages' }> {
  const items: T[] = [];
  let pages = 0;
  let stopped: 'exhausted' | 'max_pages' = 'exhausted';
  for await (const item of iteratePages(fetchPage, {
    ...options,
    onProgress: (page, pageNumber) => {
      pages = pageNumber;
      options.onProgress?.(page, pageNumber);
    },
  })) {
    items.push(item);
  }
  if (pages >= (options.maxPages ?? MAX_PAGES_PER_ITERATION)) {
    stopped = 'max_pages';
  }
  return { items, pages, stopped_reason: stopped };
}

export async function firstPage<T>(
  fetchPage: PageFetcher<T>,
  options: { limit?: number | undefined } = {},
): Promise<T | null> {
  const page = await fetchPage({ limit: options.limit ?? 1 });
  const [first] = page.items;
  return first === undefined ? null : first;
}
